import { Card, SkeletonLoader } from '@payaca/ambrosia';
import UntitledIcon from '@payaca/untitled-icons';
import { clstx } from '@payaca/utilities/cvaMerge';
import { AnimatePresence, HTMLMotionProps, motion } from 'motion/react';
import React, { FC, PropsWithChildren } from 'react';
import GuardResultDisplay, {
  PipelineGuardResultGroups,
} from './GuardResultDisplay';
import PipelineStageHeader, {
  TProps as TPipelineStageHeaderProps,
} from './PipelineStageHeader';

export type TProps = {
  highlighted?: boolean;
  errorHighlighted?: boolean;
  guardResultGroups?: PipelineGuardResultGroups;
  hideChildren?: boolean;
  scrollRef?: React.Ref<HTMLDivElement>;
  projectCount?: number;
} & Omit<TPipelineStageHeaderProps, 'childCount'> &
  Omit<HTMLMotionProps<'div'>, 'onToggle'>;

const PipelineStage: FC<PropsWithChildren<TProps>> = (props) => {
  const {
    ref,
    scrollRef,
    isOpen,
    onToggle,
    stageName,
    stageValue,
    stageColour,
    children,
    className,
    highlighted,
    errorHighlighted,
    guardResultGroups,
    hideChildren,
    projectCount,
    ...rest
  } = props;

  const shouldDisplayChildren = isOpen && !hideChildren;

  return (
    <Card
      as={motion.div}
      ref={ref}
      sizeVariant="sm"
      layout
      layoutDependency={isOpen}
      className={clstx(
        'overflow-hidden',
        'bg-gray-50',
        // Ensure we only transition the properties we want to avoid layout shift issues
        // oxlint-disable-next-line payaca/no-tailwind-arbitrary-values -- Limits the transition to these properties
        'transition-[box-shadow,background-color] duration-300',
        highlighted && 'ring-2 ring-blue-800 bg-blue-50',
        errorHighlighted && 'ring-2 ring-red-800 bg-red-50',
        className
      )}
      {...rest}
    >
      <Card.Body className="pb-2">
        <PipelineStageHeader
          isOpen={isOpen}
          onToggle={onToggle}
          stageName={stageName}
          stageValue={stageValue}
          stageColour={stageColour}
          childCount={projectCount}
        />
      </Card.Body>

      <AnimatePresence mode="wait" custom={{ isOpen }}>
        {errorHighlighted && (
          <motion.div
            key="error-highlight"
            initial={{
              opacity: 0,
            }}
            animate={{
              opacity: 1,
            }}
            className="my-auto"
          >
            <Card.Body>
              {guardResultGroups && (
                <GuardResultDisplay groups={guardResultGroups} />
              )}
            </Card.Body>
          </motion.div>
        )}

        {shouldDisplayChildren && (
          <motion.div
            ref={scrollRef}
            key="children"
            initial="initial"
            animate="animate"
            exit="exit"
            variants={PipelineStageChildrenVariants}
            className="flex-1 overflow-y-auto"
          >
            <Card.Body className="space-y-2.5 pt-2">{children}</Card.Body>
          </motion.div>
        )}
      </AnimatePresence>
    </Card>
  );
};

const PipelineStageChildrenVariants = {
  initial: {
    opacity: 0,
  },
  animate: {
    opacity: 1,
    transition: {
      delay: 0.3,
    },
  },
  exit: ({ isOpen }: { isOpen: boolean }) => {
    // when closing, have no exit animation
    if (!isOpen) {
      return {};
    }

    return {
      opacity: 0,
      transition: {
        delay: 0.3,
      },
    };
  },
};

const SkeletonLoaderPipelineStage: FC<{ className?: string }> = (props) => {
  const { className } = props;

  return (
    <Card sizeVariant="sm" className={clstx('bg-gray-50', className)}>
      <Card.Body className="space-y-4">
        <SkeletonLoader.Title />
        <SkeletonLoader.Text className="w-1/4" />

        {Array.from({ length: 6 }).map((_, index) => (
          <SkeletonLoader key={index} className="h-24 w-full rounded-lg" />
        ))}
      </Card.Body>
    </Card>
  );
};

export default Object.assign(PipelineStage, {
  SkeletonLoader: SkeletonLoaderPipelineStage,
});
